import type {
  FieldDefinitions,
  FieldErrors,
  FieldValidation,
  FormValues,
  RequiredIf,
} from "./types";

/**
 * The port of poppy_validate_field() and poppy_field_required_if_met() from
 * includes/forms.php: the rules a field definition carries, in the order the
 * server checked them, with the same copy.
 *
 * It is plain TypeScript with no browser or React in it, so the step validator
 * in the form and a future server-side handler can both run the same rules.
 */

const DEFAULT_REQUIRED = "This field is required.";

/** PHP's empty() for a submitted string: "" and "0" both count as unanswered. */
function isEmpty(value: string) {
  return value === "" || value === "0";
}

/**
 * Resolve a "required_if" rule against the submission.
 *
 * Example - required only when the organisation type select is "Other":
 *
 *   required_if: { field: "organisation_type", value: "other" }
 */
export function requiredIfMet(
  rule: RequiredIf | undefined,
  values: FormValues,
): boolean {
  if (!rule || !rule.field) {
    return false;
  }

  const actual = values[rule.field] ?? "";
  const compare = rule.compare ?? "==";

  if (compare === "not_empty") {
    return actual !== "";
  }

  const expected = Array.isArray(rule.value) ? rule.value : [rule.value ?? ""];

  if (compare === "!=") {
    return !expected.includes(actual);
  }

  return expected.includes(actual);
}

/** One field's rules. Returns the error message, or "" when the value passes. */
export function validateField(
  value: string,
  validation: FieldValidation,
  values: FormValues = {},
): string {
  let required = !!validation.required;
  const type = validation.type ?? "text";
  const messages = validation.messages ?? {};

  let conditionallyRequired = false;

  if (!required && validation.required_if) {
    conditionallyRequired = requiredIfMet(validation.required_if, values);
    required = conditionallyRequired;
  }

  if (required && isEmpty(value)) {
    const key =
      conditionallyRequired && messages.required_if
        ? "required_if"
        : "required";

    return messages[key] || DEFAULT_REQUIRED;
  }

  if (
    (type === "text" || type === "paragraph") &&
    validation.max_length &&
    value.length > validation.max_length
  ) {
    return `This field must be less than ${validation.max_length} characters.`;
  }

  if (
    type === "email" &&
    !isEmpty(value) &&
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
  ) {
    return messages.email || "Please enter a valid email address.";
  }

  if (type === "ukprn" && !isEmpty(value) && !/^\d{8}$/.test(value.trim())) {
    return messages.ukprn || "Please enter a valid eight-digit UKPRN.";
  }

  if (
    type === "checkbox" &&
    required &&
    ["", "0", "false", "off", "no"].includes(value.toLowerCase())
  ) {
    return messages.checkbox || DEFAULT_REQUIRED;
  }

  if (
    (type === "select" || type === "radio") &&
    validation.options &&
    !isEmpty(value)
  ) {
    if (!Object.keys(validation.options).includes(value)) {
      return messages.options || "Please select one of the available options.";
    }
  }

  if (validation.equals && value !== validation.equals) {
    return messages.equals || "Please select the required option to continue.";
  }

  return "";
}

/**
 * Every rule of every field, optionally limited to one step. Walking the
 * definitions rather than the values is the point: a field that never arrived
 * is still a field with rules, so leaving it out fails its "required" rule.
 */
export function validateFields(
  fields: FieldDefinitions,
  values: FormValues,
  step?: number,
): FieldErrors {
  const errors: FieldErrors = {};

  for (const [name, field] of Object.entries(fields)) {
    if (!field.validation || (step !== undefined && field.step !== step)) {
      continue;
    }

    const message = validateField(values[name] ?? "", field.validation, values);

    if (message) {
      errors[name] = message;
    }
  }

  return errors;
}

/** The step a form should be sent back to: the earliest one holding an error. */
export function lowestStep(
  fields: FieldDefinitions,
  errorNames: string[],
): number {
  let lowest: number | null = null;

  for (const [name, field] of Object.entries(fields)) {
    if (!errorNames.includes(name) || !field.step) {
      continue;
    }

    if (lowest === null || field.step < lowest) {
      lowest = field.step;
    }
  }

  return lowest ?? 1;
}
